import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSpring } from 'react-spring';
import { group, bin } from 'd3-array';
import { line, curve, curveBasis } from 'd3-shape';
import { useDispatch } from 'react-redux';

export const accessorPropsType = PropTypes.oneOfType([PropTypes.func, PropTypes.number]);

export const dimensionsPropsType = PropTypes.shape({
  height: PropTypes.number,
  width: PropTypes.number,
  marginTop: PropTypes.number,
  marginRight: PropTypes.number,
  marginBottom: PropTypes.number,
  marginLeft: PropTypes.number,
});

export const combineChartDimensions = dimensions => {
  let parsedDimensions = {
    marginTop: 40,
    marginRight: 30,
    marginBottom: 40,
    marginLeft: 75,
    ...dimensions,
  };

  return {
    ...parsedDimensions,
    boundedHeight: Math.max(parsedDimensions.height - parsedDimensions.marginTop - parsedDimensions.marginBottom, 0),
    boundedWidth: Math.max(parsedDimensions.width - parsedDimensions.marginLeft - parsedDimensions.marginRight, 0),
  };
};

export const padExtent = ([min, max], paddingFactor) => {
  const delta = Math.abs(max - min);
  const padding = delta * paddingFactor;
  return [min - padding, max + padding];
};

export const clusterKeys = data => {
  const key = 'cluster_id';
  const allKeys = [...new Map(data.map(item => [item[key], item])).values()];
  const uniqueClusterValues = allKeys.flatMap(x => x.cluster_id);
  return uniqueClusterValues;
};

/**
 * Custom hook that uses a ResizeObserver to detect when the container
 * changes size. This may require a polyfill to work correctly on unsupported browsers.
 */

export const useChartDimensions = ref => {
  //const dispatch = useDispatch();
  const [{ width, height }, setWidthHeight] = useState({
    width: 0,
    height: 0,
  });

  useLayoutEffect(() => {
    const divNode = ref.current;
    if (divNode == null) return;

    // create a resize observer
    const observer = new ResizeObserver(entries => {
      if (!Array.isArray(entries)) return;
      if (!entries.length) return;

      // on resize, update our internal state with the latest values

      const { width, height } = entries[0].contentRect;
      if (
        (width !== entries[0].contentRect.width && height !== entries[0].contentRect.height) ||
        (width !== 0 && height !== 0)
      ) {
        // dispatch({ type: 'width', payload: width });
        // dispatch({ type: 'height', payload: height });
        setWidthHeight({ width, height });
      }
    });

    // observe our container node
    observer.observe(divNode);

    // cleanup
    return () => {
      console.log('un observer');
      observer.unobserve(divNode);
    };
  }, [ref]);

  return { width, height };
};

//export default useChartDimensions;

/** determine number of ticks based on the available space */

export const numTicksForPixels = (pixelsAvailable, pixelsPerTick = 70) => {
  Math.floor(Math.abs(pixelsAvailable) / pixelsPerTick);
};

/* fancy way to get a nice svg text stroke **/
export const OutlinedSvgText = ({ stroke, strokeWidth, children, ...other }) => {
  return (
    <>
      <text stroke={stroke} strokeWidth={strokeWidth} {...other}>
        {children}
      </text>

      <text {...other}>{children}</text>
    </>
  );
};

/** basic legend component that uses CSS grid */
export const OrdinalLegend = ({ colorScale }) => {
  const domain = colorScale.domain();
  console.log({ domain });
  return (
    <div
      className="grid grid-flow-row gap-1 text-xs leading-none text-gray-600 auto-cols-max"
      style={{
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
      }}
    >
      {domain.map(category => {
        return (
          <div key={category} className="flex items-center space-x-1">
            <div style={{ backgroundColor: colorScale(category) }} className="w-2 h-2 rounded-sm" />
            <div>{category}</div>
          </div>
        );
      })}
    </div>
  );
};

export function useAnimatedPath({ toggle, delay, color }) {
  const [length, setLength] = useState(null);
  const animatedStyle = useSpring({
    strokeDashoffset: toggle ? 0 : length,
    strokeDasharray: length,
    delay,
    stroke: color,
    strokeLinejoin: 'round',
  });

  return {
    style: animatedStyle,
    ref: ref => {
      // The ref is `null` on component unmount
      if (ref) {
        setLength(ref.getTotalLength());
      }
    },
  };
}

const ALL_CHANNELS = ['Ch1', 'Ch2', 'Ch3', 'Ch4', 'Ch5', 'Ch6'];

export const detectChannels = graphData => {
  if (!graphData || !Array.isArray(graphData) || graphData.length === 0) return ['Ch1', 'Ch2'];
  return ALL_CHANNELS.filter(ch => graphData.some(d => d[ch] != null));
};

export const getGraphDataAsMap = graphData => {
  if (!graphData || !Array.isArray(graphData) || graphData.length === 0) return group([], d => d.type);
  const channels = detectChannels(graphData);
  const chart_data = channels.flatMap(ch =>
    graphData.map(d => ({
      x: d.timestamps / 1000000,
      y: d[ch],
      sign: d.cluster_id > 0 ? 'Positive' : 'Negative',
      type: ch,
      channel: d[ch],
    }))
  );
  return group(chart_data, d => d.type);
};

export const getGraphDataPerChannel = (graphData, xChannel, yChannel) => {
  if (!graphData || !Array.isArray(graphData)) return [];
  return graphData.map(d => ({
    x: d[xChannel],
    y: d[yChannel],
    type: `${xChannel}${yChannel}`,
    cluster_id: d.cluster_id,
  }));
};

export const dataPerChannel = (data, channel) => {
  const chosenChannelData = [...data].filter(entry => {
    if (entry[0] === channel) {
      return entry[1];
    }
    return null;
  });
  if (!chosenChannelData.length) return null;
  return chosenChannelData[0][1];
};

export const getColor = (d, data, graphType, uniqueClusterValues) => {
  switch (graphType) {
    // case '2D': {

    //     const color = d.cluster_id === uniqueClusterValues[0]
    //         ? 'rgb(16,20,228)'
    //         : d.cluster_id === uniqueClusterValues[1]
    //             ? 'rgb(84,222,75)'
    //             : d.cluster_id === uniqueClusterValues[2]
    //                 ? '#e45656'
    //                 : 'rgb(144,112,110)'
    //     return color;

    case '2D': {
      const color =
        d.cluster_id === 0
          ? 'rgb(16,20,228)'
          : d.cluster_id === 1
            ? 'rgb(84,222,75)'
            : d.cluster_id === 2
              ? '#e45656'
              : 'rgb(144,112,110)';
      return color;
    }

    case '1D': {
      const color = d.sign === 'Positive' ? 'rgb(0,0,255)' : 'rgb(255,0,0)';
      return color;
    }
    default:
      return null;
  }
};

export const binned = (data, num_bins, channel) => {
  // to insure that the shaded area lies "UNDER" the curve,
  // we can add an "empty" bin on the left and right side
  let naive_binned = bin().thresholds(10)(data.map(x => x.channel));

  let bin_width = naive_binned[0].x1 - naive_binned[0].x0;
  let first_bin = [];
  first_bin.x0 = naive_binned[0].x0 - bin_width;
  first_bin.x1 = naive_binned[0].x0;
  let last_bin = [];
  last_bin.x0 = naive_binned[naive_binned.length - 1].x1;
  last_bin.x1 = naive_binned[naive_binned.length - 1].x1 + bin_width;
  let binned = [first_bin].concat(naive_binned).concat(last_bin);

  return binned;
};

export const deriveLineFunction = (xScale, yScale, index) => {
  let line_path = line()
    .curve(curveBasis)
    .x(index => xScale((index.x0 + index.x1) / 2))
    .y(index => yScale(index.length));

  console.log({ line_path });

  return line_path;
};
